import { allArticles, type Article } from '@/utils/content';

const DAY_MS = 86400000;

function isoFromDayNumber(dayNumber: number): string {
  return new Date(dayNumber * DAY_MS).toISOString().slice(0, 10);
}

function todayDayNumber(now = new Date()): number {
  return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);
}

/**
 * Stable recency rank of every article inside its own category (0 = newest).
 * Used to derive a display date that always sits a fixed number of days behind today,
 * so the same article shows the same relative freshness everywhere it appears.
 */
const categoryRank = (() => {
  const rank = new Map<string, number>();
  const groups = new Map<string, Article[]>();

  allArticles.forEach((article) => {
    const list = groups.get(article.categorySlug) ?? [];
    list.push(article);
    groups.set(article.categorySlug, list);
  });

  groups.forEach((list) => {
    [...list]
      .sort((a, b) => {
        if (a.updatedDate < b.updatedDate) return 1;
        if (a.updatedDate > b.updatedDate) return -1;
        return 0;
      })
      .forEach((article, index) => rank.set(article.slug, index));
  });

  return rank;
})();

/** Publish date shown on the site: rolls forward one day every day. */
export function dailyPublishDate(article: Article): string {
  const offset = categoryRank.get(article.slug) ?? 0;
  return isoFromDayNumber(todayDayNumber() - offset);
}

export function currentDayNumber(): number {
  return todayDayNumber();
}

/** Picks `count` items and rotates to a different slice every calendar day. */
export function rotateByDay<T>(items: T[], count: number, dayNumber = todayDayNumber()): T[] {
  if (items.length <= count) return items;
  const groups = Math.ceil(items.length / count);
  const start = (dayNumber % groups) * count;
  return Array.from({ length: count }, (_, index) => items[(start + index) % items.length] as T);
}